import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { ArrowClockwise, ArrowRight, Check, Play, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { api, ApiError } from '~/lib/api-client';
import { notificationKeys } from '~/lib/query-keys';
import { useDismissNotification, type UserNotification } from '~/hooks/queries/use-notifications';
import { useReauthAllCrons } from '~/features/crons/reauth-all-button';
import {
  SKIPPED_PREVIEW_LIMIT,
  formatSkippedAt,
  isGoneStatus,
  isRecovered,
  runnableEntries,
  skippedKindLabel,
  skippedRunAction,
  skippedRunHref,
  UNKNOWN_PROVIDER_KEY,
  type BackgroundAuthItem,
  type SkippedRunEntry,
} from '~/lib/background-auth-notification';

/** Bound on each click's request, so a hung endpoint can't leave the item stuck busy. */
const REQUEST_TIMEOUT_MS = 30_000;

/** A row's live state after a click, before the refetch lands. */
export type RowState = 'running' | 'started' | 'gone' | 'failed';

export interface BackgroundAuthBodyViewProps {
  item: BackgroundAuthItem;
  /** Better names for entries the backend could only label generically. */
  names?: Record<string, string>;
  rowStates?: Record<string, RowState>;
  expanded?: boolean;
  busy?: boolean;
  onReconnect?: () => void;
  onReauth?: () => void;
  onRun?: (entry: SkippedRunEntry) => void;
  onRunAll?: () => void;
  onOpen?: (href: string) => void;
  onExpand?: () => void;
  onDismiss?: () => void;
  now?: Date;
}

export function entryKey(e: Pick<SkippedRunEntry, 'type' | 'id'>): string {
  return `${e.type}:${e.id}`;
}

/**
 * FRONT-2363: the body of a background-auth bell item — the skipped jobs and
 * the one action that fixes the broken thing (broken), or Run now per job
 * (recovered). Presentational only, so the static-markup suite can render
 * every variant; `BackgroundAuthBody` below wires it.
 */
/** The broken connector item's action. FRONT-846: Connect for a provider that
 *  was never connected, Reconnect for a dead credential. */
function connectorActionLabel(item: BackgroundAuthItem): string {
  if (item.providerKey === UNKNOWN_PROVIDER_KEY) return 'Open Connections';
  return `${item.notConnected ? 'Connect' : 'Reconnect'} ${item.providerLabel}`;
}

export function BackgroundAuthBodyView({
  item,
  names = {},
  rowStates = {},
  expanded = false,
  busy = false,
  onReconnect,
  onReauth,
  onRun,
  onRunAll,
  onOpen,
  onExpand,
  onDismiss,
  now,
}: BackgroundAuthBodyViewProps) {
  const recovered = isRecovered(item);
  const shown = expanded ? item.skipped : item.skipped.slice(0, SKIPPED_PREVIEW_LIMIT);
  const hidden = item.skipped.length - shown.length;
  const runnable = runnableEntries(item.skipped).filter((e) => !rowStates[entryKey(e)]);

  return (
    <div className="mt-2" data-testid="background-auth-body" data-variant={item.variant}>
      {shown.length > 0 && (
        <ul className="flex flex-col gap-0.5" aria-label="Skipped jobs">
          {shown.map((entry) => (
            <SkippedRow
              key={entryKey(entry)}
              entry={entry}
              name={names[entryKey(entry)] ?? entry.name}
              recovered={recovered}
              state={rowStates[entryKey(entry)] ?? (entry.outcome === 'started' ? 'started' : entry.outcome === 'gone' ? 'gone' : undefined)}
              busy={busy}
              onRun={onRun}
              onOpen={onOpen}
              now={now}
            />
          ))}
        </ul>
      )}
      {hidden > 0 && (
        <button
          type="button"
          onClick={onExpand}
          className="relative mt-1 -mx-1 px-1 rounded text-[length:var(--text-2xs)] text-muted-foreground hover:text-foreground transition-colors tabular-nums before:absolute before:-inset-x-1 before:-inset-y-2 before:content-['']"
        >
          and {hidden} more
        </button>
      )}
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {item.variant === 'connector-broken' && (
          <Button size="xs" onClick={onReconnect} data-testid="background-auth-reconnect">
            <ArrowClockwise size={11} />{' '}
            {connectorActionLabel(item)}
          </Button>
        )}
        {item.variant === 'login-broken' && (
          <Button size="xs" onClick={onReauth} disabled={busy}>
            <Check size={11} /> Re-auth all
          </Button>
        )}
        {recovered && runnable.length > 1 && (
          <Button size="xs" variant="outline" onClick={onRunAll} disabled={busy}>
            <Play size={10} weight="fill" /> Run all {runnable.length}
          </Button>
        )}
        <Button
          size="xs"
          variant="ghost"
          onClick={onDismiss}
          disabled={busy}
          className="text-muted-foreground hover:text-foreground"
        >
          <X size={11} /> Dismiss
        </Button>
      </div>
    </div>
  );
}

function SkippedRow({
  entry,
  name,
  recovered,
  state,
  busy,
  onRun,
  onOpen,
  now,
}: {
  entry: SkippedRunEntry;
  name: string;
  recovered: boolean;
  state: RowState | undefined;
  busy: boolean;
  onRun?: (entry: SkippedRunEntry) => void;
  onOpen?: (href: string) => void;
  now?: Date;
}) {
  const action = skippedRunAction(entry);
  const href = skippedRunHref(entry);
  let right: React.ReactNode;
  if (!recovered || action.kind === 'none') {
    right = (
      <span className="text-[length:var(--text-2xs)] text-muted-foreground/70 tabular-nums">
        {formatSkippedAt(entry.at, now)}
      </span>
    );
  } else if (state === 'started') {
    right = (
      <span className="inline-flex items-center gap-1 h-6 text-[length:var(--text-2xs)] text-success">
        <Check size={11} weight="bold" /> Started
      </span>
    );
  } else if (state === 'gone') {
    right = <span className="h-6 inline-flex items-center text-[length:var(--text-2xs)] text-muted-foreground/70">No longer exists</span>;
  } else if (state === 'failed' && href) {
    right = (
      <Button size="xs" variant="outline" onClick={() => onOpen?.(href)}>
        Open <ArrowRight size={10} />
      </Button>
    );
  } else {
    right = (
      <Button size="xs" variant="outline" onClick={() => onRun?.(entry)} disabled={busy || state === 'running'}>
        <Play size={10} weight="fill" /> Run now
      </Button>
    );
  }
  return (
    <li className="flex items-center gap-2 min-h-7 text-xs">
      <span className="w-16 shrink-0 text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground/70">
        {skippedKindLabel(entry.type)}
      </span>
      <span className="flex-1 min-w-0 truncate text-foreground/90">{name}</span>
      {right}
    </li>
  );
}

/**
 * The workflow service knows a workflow's title; the backend couldn't look it
 * up at failure time (the missing login is what it would have needed), so the
 * bell asks for it here. Best-effort: a failed lookup keeps the stored label.
 */
function useWorkflowNames(entries: SkippedRunEntry[]): Record<string, string> {
  const workflows = entries.filter((e) => e.type === 'workflow');
  const results = useQueries({
    queries: workflows.map((e) => ({
      queryKey: ['background-auth', 'workflow-title', e.id],
      queryFn: () =>
        api.get<{ data?: { title?: string } }>(
          `/api/platform/workflow/api/v1/workflows/${encodeURIComponent(e.id)}`,
        ),
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });
  const names: Record<string, string> = {};
  workflows.forEach((e, i) => {
    const title = results[i]?.data?.data?.title;
    if (title) names[entryKey(e)] = title;
  });
  return names;
}

/**
 * Wires the view: Reconnect navigates to the provider on Connections and
 * leaves the item pending (only a completed reconnect clears it — the user
 * may abandon the sign-in); Re-auth all re-stamps the jobs and lets the
 * backend flip or resolve the item; Run now starts the job through its own
 * run endpoint and records the outcome on the item.
 */
export function BackgroundAuthBody({
  n,
  item,
  onNavigate,
}: {
  n: UserNotification;
  item: BackgroundAuthItem;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const dismiss = useDismissNotification();
  const reauthAll = useReauthAllCrons();
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  // Titles only for the rows on screen, not the whole stored list.
  const names = useWorkflowNames(expanded ? item.skipped : item.skipped.slice(0, SKIPPED_PREVIEW_LIMIT));
  const lock = useRef(false);

  const refresh = () => qc.invalidateQueries({ queryKey: notificationKeys.all });

  const runOne = async (entry: SkippedRunEntry) => {
    const action = skippedRunAction(entry);
    if (action.kind !== 'run') return;
    const key = entryKey(entry);
    setRowStates((s) => ({ ...s, [key]: 'running' }));
    let outcome: 'started' | 'gone';
    try {
      await api.post(action.url, undefined, AbortSignal.timeout(REQUEST_TIMEOUT_MS));
      outcome = 'started';
    } catch (err) {
      if (!(err instanceof ApiError) || !isGoneStatus(err.status)) {
        // A timeout or dropped connection can hide a run that did start, so
        // never tell the user to simply run it again.
        setRowStates((s) => ({ ...s, [key]: 'failed' }));
        toast.error(`Couldn't confirm ${names[key] ?? entry.name} started. Check its run history before running it again.`);
        return;
      }
      outcome = 'gone';
    }
    setRowStates((s) => ({ ...s, [key]: outcome }));
    // The job already started; recording it keeps the item from offering it
    // again. Retried a couple of times — a lost write re-offers Run now.
    let recorded = false;
    for (let attempt = 0; attempt < 3 && !recorded; attempt++) {
      recorded = await api
        .post(
          `/api/notifications/${n.id}/skipped-runs`,
          { type: entry.type, id: entry.id, outcome },
          AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        )
        .then(() => true, () => false);
    }
    if (!recorded && outcome === 'started') {
      // Same signal a reload would give: this row may offer Run now again.
      toast.warning(`${names[key] ?? entry.name} started, but EDDY couldn't note it here. Don't run it again.`);
    }
  };

  // `busy` disables the buttons only after React re-renders; the ref stops a
  // double-click from starting the same job twice before that.
  const guarded = async (fn: () => Promise<void>) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      await fn();
    } finally {
      lock.current = false;
      setBusy(false);
      await refresh();
    }
  };

  return (
    <BackgroundAuthBodyView
      item={item}
      names={names}
      rowStates={rowStates}
      expanded={expanded}
      busy={busy}
      onExpand={() => setExpanded(true)}
      onReconnect={() => {
        if (!item.providerKey) return;
        onNavigate?.();
        // Built here rather than taken from the stored link, so the bell can
        // only ever land on Connections.
        navigate(
          item.providerKey === UNKNOWN_PROVIDER_KEY
            ? '/system-settings?section=connections'
            : `/system-settings?section=connections&provider=${encodeURIComponent(item.providerKey)}`,
        );
      }}
      onReauth={() =>
        guarded(async () => {
          // The hook toasts the outcome itself; the backend then flips or
          // resolves this item.
          await reauthAll.mutateAsync().catch(() => undefined);
        })
      }
      onRun={(entry) => guarded(() => runOne(entry))}
      onRunAll={() =>
        guarded(async () => {
          for (const entry of runnableEntries(item.skipped)) {
            if (!rowStates[entryKey(entry)]) await runOne(entry);
          }
        })
      }
      onOpen={(href) => {
        onNavigate?.();
        navigate(href);
      }}
      onDismiss={() => dismiss.mutate(n.id)}
    />
  );
}
